/**
 * RSVP Override Feature (temporary)
 * Client-side RSVP simulation helpers for procurement what-if analysis.
 * Delete this file when the RSVP Override Feature is retired.
 */

import type { Menu } from '@/types/api';
import { RSVP_SIZES, createRsvpSizeCounts, type RSVPSizeCode, type RSVPSizeCounts } from '@/types/units';

export type RsvpOverride = RSVPSizeCounts & {
  isAssumed: boolean;
};

/**
 * Stored simulation counts are complete assumed counts, not patches over the
 * live RSVP totals. Legacy M/L-only payloads are normalized with missing sizes
 * set to 0 because simulation mode is used when actual RSVP counts are absent.
 */
type StoredRsvpCounts = RSVPSizeCounts;

export type StoredRsvpSimulation = {
  editMode: boolean;
  overrides: Record<number, StoredRsvpCounts>;
};

const RSVP_SIMULATION_STORAGE_KEY_PREFIX = 'procurement:rsvp-simulation:';

export function getRsvpSimulationStorageKey(weekStartDate: string): string {
  return `${RSVP_SIMULATION_STORAGE_KEY_PREFIX}${weekStartDate}`;
}

export function parseRsvpInput(value: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.floor(parsed);
}

export function buildRsvpOverrides(menus: Menu[]): Record<number, RsvpOverride> {
  return Object.fromEntries(
    menus.map((menu) => {
      return [menu.id, createOverride({
        S: menu.rsvpCountS ?? 0,
        M: menu.rsvpCountM ?? 0,
        L: menu.rsvpCountL ?? 0,
        isAssumed: false,
      })];
    })
  );
}

function createOverride(counts: RSVPSizeCounts & { isAssumed: boolean }): RsvpOverride {
  return { ...createRsvpSizeCounts(counts), isAssumed: counts.isAssumed };
}

function parseStoredRsvpCount(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  return Math.floor(value);
}

export function readStoredRsvpSimulation(weekStartDate: string): StoredRsvpSimulation | null {
  if (typeof window === 'undefined') return null;

  try {
    const rawValue = window.localStorage.getItem(getRsvpSimulationStorageKey(weekStartDate));
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue) as {
      editMode?: unknown;
      overrides?: unknown;
    };

    if (typeof parsed?.editMode !== 'boolean') return null;
    if (!parsed.overrides || typeof parsed.overrides !== 'object' || Array.isArray(parsed.overrides)) {
      return null;
    }

    const overrides: StoredRsvpSimulation['overrides'] = {};
    for (const [menuId, value] of Object.entries(parsed.overrides as Record<string, unknown>)) {
      const parsedMenuId = Number(menuId);
      if (!Number.isInteger(parsedMenuId) || parsedMenuId < 1) continue;
      if (!value || typeof value !== 'object' || Array.isArray(value)) continue;

      const legacy = value as { mediumCount?: unknown; largeCount?: unknown };
      const counts: Partial<RSVPSizeCounts> = {};
      let hasParsedCount = false;
      for (const size of RSVP_SIZES) {
        const parsed = parseStoredRsvpCount((value as Partial<Record<RSVPSizeCode, unknown>>)[size]);
        if (parsed !== null) {
          counts[size] = parsed;
          hasParsedCount = true;
        }
      }

      if (counts.M === undefined) {
        const parsedLegacyMedium = parseStoredRsvpCount(legacy.mediumCount);
        if (parsedLegacyMedium !== null) {
          counts.M = parsedLegacyMedium;
          hasParsedCount = true;
        }
      }
      if (counts.L === undefined) {
        const parsedLegacyLarge = parseStoredRsvpCount(legacy.largeCount);
        if (parsedLegacyLarge !== null) {
          counts.L = parsedLegacyLarge;
          hasParsedCount = true;
        }
      }
      if (!hasParsedCount) continue;

      overrides[parsedMenuId] = createRsvpSizeCounts(counts);
    }

    return {
      editMode: parsed.editMode,
      overrides,
    };
  } catch {
    return null;
  }
}

export function writeStoredRsvpSimulation(
  weekStartDate: string,
  simulation: StoredRsvpSimulation
): void {
  if (typeof window === 'undefined') return;

  try {
    if (Object.keys(simulation.overrides).length === 0) {
      window.localStorage.removeItem(getRsvpSimulationStorageKey(weekStartDate));
      return;
    }

    window.localStorage.setItem(
      getRsvpSimulationStorageKey(weekStartDate),
      JSON.stringify(simulation)
    );
  } catch {
    // Simulation should remain usable even if localStorage is unavailable.
  }
}

export function applyStoredRsvpSimulation(
  menus: Menu[],
  storedSimulation: StoredRsvpSimulation | null
): Record<number, RsvpOverride> {
  const overrides = buildRsvpOverrides(menus);
  if (!storedSimulation) return overrides;

  for (const [menuId, value] of Object.entries(storedSimulation.overrides)) {
    const parsedMenuId = Number(menuId);
    if (!overrides[parsedMenuId]) continue;

    overrides[parsedMenuId] = {
      ...createRsvpSizeCounts(value),
      isAssumed: false,
    };
  }

  return overrides;
}

export function createStoredRsvpSimulation(
  actualOverrides: Record<number, RsvpOverride>,
  currentOverrides: Record<number, RsvpOverride>,
  editMode: boolean
): StoredRsvpSimulation {
  const overrides: StoredRsvpSimulation['overrides'] = {};

  for (const [menuId, currentValue] of Object.entries(currentOverrides)) {
    const parsedMenuId = Number(menuId);
    const actualValue = actualOverrides[parsedMenuId];
    if (!actualValue) continue;

    if (
      RSVP_SIZES.some((size) => currentValue[size] !== actualValue[size])
    ) {
      overrides[parsedMenuId] = createRsvpSizeCounts(currentValue);
    }
  }

  return {
    editMode,
    overrides,
  };
}
